import { ZoomContext } from '@modules/zoom/contexts/ZoomContext';
import ZoomVideo, { LocalAudioTrack, LocalVideoTrack, MediaDevice } from '@zoom/videosdk';
import useToast from 'apps/agora/src/hooks/useToast';
import { useEffect, useRef, useState, useContext, RefObject } from 'react';
import { logger } from '../../../utils/logger';
import { classifyError, getErrorToastMessage } from '../utils/errorHandler';
import { findRemovedDevicesById, mountDevices, stopTracks } from '../utils/helpers';

interface UseLocalTracksProps {
  videoRef: RefObject<any>;
  initializeVideo?: boolean;
  initializeAudio?: boolean;
  isCameraActive?: boolean;
  isMicrophoneActive?: boolean;
  activeCamera?: string;
  activeMicrophone?: string;
  onActiveMicrophoneChange?: (microphoneId: string) => void | Promise<void>;
  onActiveCameraChange?: (cameraId: string, shouldStartCamera?: boolean) => void | Promise<void>;
  onSetIsCameraActive?: (isActive: boolean) => void;
  onSetIsMicrophoneActive?: (isActive: boolean) => void;
}

interface DeviceError {
  deviceLabel: string;
  name: string;
  message: string;
}

const useLocalTracks = (props: UseLocalTracksProps) => {
  const {
    initializeVideo = true,
    initializeAudio = true,
    isCameraActive = false,
    isMicrophoneActive = false,
    activeCamera,
    activeMicrophone,
    onSetIsCameraActive,
    onSetIsMicrophoneActive,
    onActiveMicrophoneChange,
    onActiveCameraChange,
    videoRef,
  } = props;

  const [micList, setMicList] = useState<MediaDevice[]>([]);
  const [cameraList, setCameraList] = useState<MediaDevice[]>([]);
  const [initialSetupCompleted, setInitialSetupCompleted] = useState(false);

  const [isCameraStateSwitchLoading, setIsCameraStateSwitchLoading] = useState(false);
  const [isMicrophoneStateSwitchLoading, setIsMicrophoneStateSwitchLoading] = useState(false);

  const [isCameraSwitchLoading, setIsCameraSwitchLoading] = useState(false);
  const [isMicrophoneSwitchLoading, setIsMicrophoneSwitchLoading] = useState(false);

  const [error, setError] = useState<{
    name: string;
    message: string;
  }>();

  const [camerasWithErrors, setCamerasWithErrors] = useState<Record<string, DeviceError>>({});

  const [microphonesWithErrors, setMicrophonesWithErrors] = useState<Record<string, DeviceError>>(
    {}
  );

  const streamRef = useRef<MediaStream | undefined>();
  const isMountedRef = useRef(false);

  const localAudioRef = useRef<LocalAudioTrack | null>(null);
  const localVideoRef = useRef<LocalVideoTrack | null>(null);

  const { setCanJoinMeeting, logError } = useContext(ZoomContext);
  const toast = useToast();

  const removeCameraErrorIfPresent = (deviceId: string) => {
    if (camerasWithErrors[deviceId]) {
      setCamerasWithErrors((prev) => {
        const newCameraObject = { ...prev };

        delete newCameraObject[deviceId];

        return newCameraObject;
      });
    }
  };

  const removeMicrophoneErrorIfPresent = (deviceId: string) => {
    if (microphonesWithErrors[deviceId]) {
      setMicrophonesWithErrors((prev) => {
        const newMicrophoneObject = { ...prev };

        delete newMicrophoneObject[deviceId];

        return newMicrophoneObject;
      });
    }
  };

  const addCameraError = (deviceId: string, label: string, error: DeviceError) => {
    setCamerasWithErrors((prev) => ({
      ...prev,
      [deviceId]: {
        deviceLabel: label,
        name: error.name,
        message: error.message,
      },
    }));
  };

  const addMicrophoneError = (deviceId: string, label: string, error: DeviceError) => {
    setMicrophonesWithErrors((prev) => ({
      ...prev,
      [deviceId]: {
        deviceLabel: label,
        name: error.name,
        message: error.message,
      },
    }));
  };

  const tryInitializeAudio = async (devices: MediaDevice[], index = 0) => {
    if (index >= devices.length) {
      const videoError = classifyError(
        { type: 'NO_WORKING_MICROPHONE' },
        {
          operation: 'Microphone initialization',
          retryHandler: () => {
            setIsMicrophoneStateSwitchLoading(true);
            tryInitializeAudio(devices, 0);
          },
        }
      );
      toast.error(getErrorToastMessage(videoError));
      setIsMicrophoneStateSwitchLoading(false);
      return;
    }

    const deviceId = devices[index].deviceId;
    const audioTrack = ZoomVideo.createLocalAudioTrack(deviceId);
    localAudioRef.current = audioTrack;

    try {
      await audioTrack.start();

      if (!isMountedRef.current) return;

      await audioTrack.unmute();

      if (!isMountedRef.current) return;

      onSetIsMicrophoneActive?.(true);

      await onActiveMicrophoneChange?.(deviceId);

      setIsMicrophoneStateSwitchLoading(false);

      removeMicrophoneErrorIfPresent(deviceId);
    } catch (error: any) {
      logError(error);
      audioTrack.stop().catch((error) => {
        logError(error);
        logger.error('Error stopping audio track:', error);
      });
      localAudioRef.current = null;

      addMicrophoneError(deviceId, devices[index].label, error);

      // Retry with the next device
      await tryInitializeAudio(devices, index + 1);
    }
  };

  const tryInitializeVideo = async (devices: MediaDevice[], index = 0) => {
    if (index >= devices.length) {
      const videoError = classifyError(
        { type: 'NO_WORKING_CAMERA' },
        {
          operation: 'Camera initialization',
          retryHandler: () => {
            setIsCameraStateSwitchLoading(true);
            tryInitializeVideo(devices, 0);
          },
        }
      );
      toast.error(getErrorToastMessage(videoError));
      setIsCameraStateSwitchLoading(false);
      return;
    }

    const deviceId = devices[index].deviceId;
    const videoTrack = ZoomVideo.createLocalVideoTrack(deviceId);
    localVideoRef.current = videoTrack;

    try {
      await videoTrack.start(videoRef.current);
      if (!isMountedRef.current) return;

      onSetIsCameraActive?.(true);
      await onActiveCameraChange?.(deviceId);
      setIsCameraStateSwitchLoading(false);

      removeCameraErrorIfPresent(deviceId);
    } catch (error: any) {
      logError(error);
      videoTrack.stop().catch((error) => {
        logError(error);
        logger.error('Error stopping video track:', error);
      });
      localVideoRef.current = null;

      addCameraError(deviceId, devices[index].label, error);

      // Retry with the next device
      await tryInitializeVideo(devices, index + 1);
    }
  };

  const initializeDevices = () => {
    if (isCameraStateSwitchLoading || isMicrophoneStateSwitchLoading) {
      return;
    }

    setInitialSetupCompleted(false);
    setIsCameraStateSwitchLoading(true);
    setIsMicrophoneStateSwitchLoading(true);
    setCanJoinMeeting(false);

    mountDevices(streamRef.current, 5)
      .then(async ({ microphones, cameras, stream }) => {
        if (!isMountedRef.current) return;

        streamRef.current = stream;

        if (initializeAudio && microphones.length > 0) {
          setMicList(microphones);

          await tryInitializeAudio(microphones);
        }

        if (initializeVideo && videoRef.current && cameras.length > 0) {
          setCameraList(cameras);

          await tryInitializeVideo(cameras);
        }

        if (!isMountedRef.current) return;

        setInitialSetupCompleted(true);
      })
      .catch((error) => {
        logError(error);
        setIsCameraStateSwitchLoading(false);
        setIsMicrophoneStateSwitchLoading(false);
        setError({ name: error.name, message: error.message });
      })
      .finally(() => setCanJoinMeeting(true));
  };

  useEffect(() => {
    if (!videoRef.current) {
      return;
    }

    isMountedRef.current = true;

    initializeDevices();

    return () => {
      isMountedRef.current = false;

      stopTracks(streamRef.current);

      localAudioRef.current?.stop().catch((error) => {
        logError(error);
        logger.error('Error stopping local audio track:', error);
      });
      localVideoRef.current?.stop().catch((error) => {
        logError(error);
        logger.error('Error stopping local video track:', error);
      });
    };
  }, []);

  useEffect(() => {
    if (!videoRef.current) {
      return;
    }

    const deviceChangeHandler = async () => {
      if (!initialSetupCompleted) {
        return;
      }

      setCanJoinMeeting(false);

      try {
        const { microphones, cameras } = await mountDevices(streamRef.current);

        if (microphones.length !== micList.length) {
          if (microphones.length < micList.length) {
            const removedMicrophone = findRemovedDevicesById(micList, microphones);
            const fallbackMicrophone = microphones[0];

            if (activeMicrophone === removedMicrophone) {
              if (fallbackMicrophone) {
                const audioTrack = ZoomVideo.createLocalAudioTrack(fallbackMicrophone.deviceId);

                localAudioRef.current = audioTrack;

                try {
                  await audioTrack.start();

                  await onActiveMicrophoneChange?.(fallbackMicrophone.deviceId);

                  if (isMicrophoneActive) {
                    await audioTrack.unmute();
                  }

                  removeMicrophoneErrorIfPresent(fallbackMicrophone.deviceId);
                } catch (error: any) {
                  logError(error);
                  const videoError = classifyError(error, {
                    operation: 'Switch microphone',
                    deviceLabel: fallbackMicrophone.label,
                    deviceId: fallbackMicrophone.deviceId,
                  });
                  addMicrophoneError(fallbackMicrophone.deviceId, fallbackMicrophone.label, error);
                  toast.error(getErrorToastMessage(videoError));
                  logger.error('Failed to start or unmute new audio track:', error);
                }
              } else {
                localAudioRef.current = null;
                onSetIsMicrophoneActive?.(false);
              }
            }
          }
          setMicList(microphones);
        }

        if (cameras.length !== cameraList.length) {
          if (cameras.length < cameraList.length) {
            const removedCamera = findRemovedDevicesById(cameraList, cameras);
            const fallbackCamera = cameras[0];

            if (activeCamera === removedCamera) {
              if (isCameraActive) {
                try {
                  await localVideoRef.current?.stop();
                  stopTracks(streamRef.current, 'video');
                } catch (error) {
                  logError(error);
                  logger.error('Failed to stop video:', error);
                }
              }

              if (fallbackCamera) {
                const videoTrack = ZoomVideo.createLocalVideoTrack(fallbackCamera.deviceId);

                await onActiveCameraChange?.(fallbackCamera.deviceId, false);

                localVideoRef.current = videoTrack;

                if (isCameraActive) {
                  try {
                    await videoTrack?.start(videoRef.current);

                    removeCameraErrorIfPresent(fallbackCamera.deviceId);
                  } catch (error: any) {
                    logError(error);
                    const videoError = classifyError(error, {
                      operation: 'Switch camera',
                      deviceLabel: fallbackCamera.label,
                      deviceId: fallbackCamera.deviceId,
                    });
                    addCameraError(fallbackCamera.deviceId, fallbackCamera.label, error);
                    toast.error(getErrorToastMessage(videoError));
                    logger.error('Error in local tracks:', error);
                  }
                }
              } else {
                localVideoRef.current = null;
                onSetIsCameraActive?.(false);
              }
            }
          }
          setCameraList(cameras);
        }
      } catch (error) {
        logError(error);
        logger.error('Failed to handle device change:', error);
      } finally {
        setCanJoinMeeting(true);
      }
    };

    navigator.mediaDevices.addEventListener('devicechange', deviceChangeHandler);

    return () => navigator.mediaDevices.removeEventListener('devicechange', deviceChangeHandler);
  }, [
    micList,
    cameraList,
    initialSetupCompleted,
    isMicrophoneActive,
    isCameraActive,
    camerasWithErrors,
    microphonesWithErrors,
    onActiveCameraChange,
  ]);

  const toggleMicrophone = async (isMicrophoneActive: boolean) => {
    if (isMicrophoneStateSwitchLoading) return;

    if (!activeMicrophone) {
      return toast.warning('Please select an input audio device.');
    }

    setIsMicrophoneStateSwitchLoading(true);

    try {
      if (isMicrophoneActive) {
        await localAudioRef.current?.mute();
      } else {
        await localAudioRef.current?.unmute();
      }

      onSetIsMicrophoneActive?.(!isMicrophoneActive);
    } catch (error: any) {
      logError(error);
      const microphoneLabel = micList.find((mic) => mic.deviceId === activeMicrophone)?.label;
      const videoError = classifyError(error, {
        operation: 'Toggle microphone',
        deviceLabel: microphoneLabel,
        deviceId: activeMicrophone,
      });

      addMicrophoneError(activeMicrophone, microphoneLabel || '', error);
      toast.error(getErrorToastMessage(videoError));
    }
    setIsMicrophoneStateSwitchLoading(false);
  };

  const toggleCamera = async (isCameraActive: boolean) => {
    if (!videoRef.current || isCameraStateSwitchLoading) return;

    if (!activeCamera) {
      return toast.warning('Please select a video device.');
    }
    setIsCameraStateSwitchLoading(true);

    try {
      if (isCameraActive) {
        await localVideoRef.current?.stop();
        stopTracks(streamRef.current, 'video');
      } else {
        await localVideoRef.current?.start(videoRef.current);

        removeCameraErrorIfPresent(activeCamera);
      }
      onSetIsCameraActive?.(!isCameraActive);
    } catch (error: any) {
      logError(error);
      const cameraLabel = cameraList.find((camera) => camera.deviceId === activeCamera)?.label;
      const videoError = classifyError(error, {
        operation: 'Toggle camera',
        deviceLabel: cameraLabel,
        deviceId: activeCamera,
      });

      addCameraError(activeCamera, cameraLabel || '', error);
      toast.error(getErrorToastMessage(videoError));
    }

    setIsCameraStateSwitchLoading(false);
  };

  const changeMicrophone = async (value: string) => {
    if (isMicrophoneSwitchLoading) return;

    setIsMicrophoneSwitchLoading(true);

    try {
      await localAudioRef.current?.stop();
    } catch (error) {
      logError(error);
    }

    try {
      const audioTrack = ZoomVideo.createLocalAudioTrack(value);
      localAudioRef.current = audioTrack;

      await audioTrack.start();

      if (isMicrophoneActive) {
        await audioTrack.unmute();
        onSetIsMicrophoneActive?.(true);
      }
      await onActiveMicrophoneChange?.(value);

      removeMicrophoneErrorIfPresent(value);
    } catch (error: any) {
      logError(error);
      const microphoneLabel = micList.find((mic) => mic.deviceId === value)?.label;
      const videoError = classifyError(error, {
        operation: 'Change microphone',
        deviceLabel: microphoneLabel,
        deviceId: value,
      });

      addMicrophoneError(value, microphoneLabel || '', error);
      toast.error(getErrorToastMessage(videoError));
      onSetIsMicrophoneActive?.(false);
    }

    setIsMicrophoneSwitchLoading(false);
  };

  const changeCamera = async (value: string, shouldStartCamera = false) => {
    if (isCameraSwitchLoading) return;

    setIsCameraSwitchLoading(true);

    if (!isCameraActive) {
      try {
        await localVideoRef.current?.stop();
      } catch (error) {
        logError(error);
      }

      const videoTrack = ZoomVideo.createLocalVideoTrack(value);
      localVideoRef.current = videoTrack;
      await onActiveCameraChange?.(value);

      setIsCameraSwitchLoading(false);
      return;
    }

    try {
      await localVideoRef.current?.switchCamera(value);

      await onActiveCameraChange?.(value, shouldStartCamera);

      removeCameraErrorIfPresent(value);
    } catch (error: any) {
      logError(error);
      if (activeCamera) {
        localVideoRef.current = ZoomVideo.createLocalVideoTrack(activeCamera);
      }

      const cameraLabel = cameraList.find((camera) => camera.deviceId === value)?.label;
      const videoError = classifyError(error, {
        operation: 'Change camera',
        deviceLabel: cameraLabel,
        deviceId: value,
      });

      addCameraError(value, cameraLabel || '', error);
      toast.error(getErrorToastMessage(videoError));
      onSetIsCameraActive?.(false);
    }

    setIsCameraSwitchLoading(false);
  };

  return {
    error,
    initialSetupCompleted,
    camerasWithErrors,
    microphonesWithErrors,
    isCameraSwitchLoading,
    isMicrophoneSwitchLoading,
    isCameraStateSwitchLoading,
    isMicrophoneStateSwitchLoading,
    micList,
    cameraList,
    retryInitializeDevices: initializeDevices,
    toggleMicrophone,
    toggleCamera,
    changeMicrophone,
    changeCamera,
  };
};

export default useLocalTracks;
